import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import axios from 'axios';
import config from '../../config.js';

import '../../styles/global.css';
import '../../styles/requests.css';
import '../../styles/status.css';
import '../../styles/pagination.css';

import PageHeader from '../../components/general/pageHeader.js';
import PageTable from '../../components/general/pageTable';
import CreateGatePassForm from '../../components/forms/createGatePassForm';
import DeleteConfirmation from '../../components/general/deleteConfirmation.js';
import useAuthenticate from '../../components/authentication/authenticate';
import Loading from '../../components/general/loading.js';

import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';

const MGatePass = () => {
  const { user, isLoading } = useAuthenticate();
  const queryClient = useQueryClient();

  const role = Object.keys(user?.user?.active || {})[0];
  const account = {
    accountId: user?.user?.accounts?.[role],
    accountName: user?.user?.email
      ?.split('@')[0]
      ?.split('.')[0]
      ?.replace(/^./, (c) => c.toUpperCase()),
  };

  const [selectedProperty, setSelectedProperty] = useState(null);
  const [addOverlayOpen, setAddOverlayOpen] = useState(false);
  const [editOverlayOpen, setEditOverlayOpen] = useState(false);
  const [deleteOverlayOpen, setDeleteOverlayOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);

  // Pagination
  const [page, setPage] = useState(1);
  const rowsPerPage = 10;

  const { data: properties } = useQuery({
    queryKey: ['properties'],
    queryFn: fetchProperties,
  });

  const { data: units } = useQuery({
    queryKey: ['units', selectedProperty?.value],
    queryFn: () => fetchUnitsByProperty(selectedProperty.value),
    enabled: !!selectedProperty,
  });

  const { data: requestsData, isLoading: isRequestsLoading } = useQuery({
    queryKey: ['requests', selectedProperty?.value, page, rowsPerPage],
    queryFn: ({ queryKey }) => {
      const [, propertyId, currentPage, limit] = queryKey;
      return fetchRequestsByProperty(propertyId, currentPage, limit);
    },
    enabled: !!selectedProperty,
    keepPreviousData: true,
  });

  useEffect(() => {
    if (properties?.length > 0) {
      setSelectedProperty(properties[0]);
    }
  }, [properties]);

  const handleEdit = (request) => {
    setSelectedRequest(request);
    setEditOverlayOpen(true);
  };

  const handleDelete = (request) => {
    setSelectedRequest(request);
    setDeleteOverlayOpen(true);
  };

  const handleRequestCreated = () => {
    queryClient.invalidateQueries(['requests', selectedProperty?.value, page, rowsPerPage]);
    setAddOverlayOpen(false);
  };

  const handleRequestUpdated = (updatedRequest) => {
    queryClient.invalidateQueries(['requests', selectedProperty?.value], { refetchInactive: true });
    setSelectedRequest(null);
  };

  const handleRequestDeleted = async () => {
    if (!selectedRequest) return;

    try {
      await axios.delete(`${config.API_URL}/v2/request/${selectedRequest._id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
      });

      setDeleteOverlayOpen(false);
      setSelectedRequest(null);
      toast.success('Gate pass deleted successfully.');
      await queryClient.invalidateQueries(['requests', selectedProperty?.value]);
    } catch (err) {
      console.error('Failed to delete gate pass:', err);
      toast.error('Failed to delete gate pass.');
    }
  };

  const totalPages = requestsData?.totalPages || 1;
  const maxVisibleButtons = 10;
  const startPage = Math.floor((page - 1) / maxVisibleButtons) * maxVisibleButtons + 1;
  const endPage = Math.min(startPage + maxVisibleButtons - 1, totalPages);

  return (
    <div className="full-page w-full">
      <PageHeader pageName="Gate Pass" />

      {isLoading ? (
        <Loading />
      ) : (
        <>
          {/* Property selector + button */}
          <div className="page-header">
            <Autocomplete
              options={properties}
              value={selectedProperty}
              onChange={(e, newVal) => {
                if (newVal && newVal.value && newVal.label) {
                  setSelectedProperty(newVal);
                  setPage(1);
                } else {
                  setSelectedProperty(null);
                }
              }}
              isOptionEqualToValue={(option, value) => option?.value === value?.value}
              getOptionLabel={(option) =>
                typeof option === 'string' ? option : option?.label || option?.name || ''
              }
              renderInput={(params) => (
                <TextField {...params} label="Select Property" variant="outlined" fullWidth />
              )}
              sx={{ width: '350px' }}
            />
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                className="header-button"
                onClick={() => {
                  setAddOverlayOpen(true);
                }}
              >
                <i className="fas fa-receipt"></i>File a Gate Pass
              </button>
            </div>
          </div>

          {isRequestsLoading ? (
            <Loading />
          ) : (
            <div className="table-wrapper">
              <PageTable
                rows={requestsData?.results || []}
                columns={[
                  { label: 'Unit', accessor: (row) => row.unit?.unitNumber || 'N/A', width: '10%' },
                  {
                    label: 'Status',
                    accessor: (row) => (
                      <span className={`request-status-badge ${row.status?.toLowerCase()}`}>
                        {row.status}
                      </span>
                    ),
                    width: '15%',
                  },
                  {
                    label: 'Requested by',
                    accessor: (row) =>
                      row.requestedBy?.user
                        ? `${row.requestedBy.user.givenName || ''} ${
                            row.requestedBy.user.lastName || ''
                          }`
                        : 'N/A',
                    width: '15%',
                  },
                  {
                    label: 'Requested On',
                    accessor: (row) => new Date(row.createdAt).toLocaleDateString(),
                    width: '15%',
                  },
                  { label: 'Description', accessor: (row) => row.description || 'N/A', width: '30%' },
                  {
                    label: 'Start Date',
                    accessor: (row) =>
                      row.details?.startDate
                        ? new Date(row.details.startDate).toLocaleDateString()
                        : 'N/A',
                    width: '15%',
                  },
                  {
                    label: 'End Date',
                    accessor: (row) =>
                      row.details?.endDate
                        ? new Date(row.details.endDate).toLocaleDateString()
                        : 'N/A',
                    width: '15%',
                  },
                ]}
                hasButtons
                hasDelete
                hasEdit
                onEdit={handleEdit}
                onDelete={handleDelete}
              />

              {/* ✅ Pagination */}
              {requestsData && requestsData.totalPages > 1 && (
                <div className="pagination-controls">
                  <div className="pagination-buttons">
                    <button
                      className="pagination-button"
                      disabled={page === 1}
                      onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                    >
                      <i className="fas fa-chevron-left"></i>
                    </button>

                    {startPage > 1 && (
                      <button className="pagination-page" onClick={() => setPage(startPage - 1)} >
                        ...
                      </button>
                    )}

                    {Array.from({ length: endPage - startPage + 1 }, (_, i) => {
                      const pageNum = startPage + i;
                      return (
                        <button
                          key={pageNum}
                          className={`pagination-page ${page === pageNum ? 'active' : ''}`}
                          onClick={() => setPage(pageNum)}
                        >
                          {pageNum}
                        </button>
                      );
                    })}

                    {endPage < totalPages && (
                      <button className="pagination-page" onClick={() => setPage(endPage + 1)} >
                        ...
                      </button>
                    )}

                    <button
                      className="pagination-button"
                      disabled={page === totalPages}
                      onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
                    >
                      <i className="fas fa-chevron-right"></i>
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Create */}
      {addOverlayOpen && selectedProperty && (
        <CreateGatePassForm
          property={{ _id: selectedProperty.value }}
          unitOptions={units}
          account={account}
          role={role}
          onRequestCreated={handleRequestCreated}
          onClose={() => setAddOverlayOpen(false)}
        />
      )}

      {/* Edit */}
      {editOverlayOpen && (
        <CreateGatePassForm
          property={{ _id: selectedProperty.value }}
          unitOptions={units}
          account={account}
          role={role}
          selectedRequest={selectedRequest}
          onRequestUpdated={handleRequestUpdated}
          onClose={() => setEditOverlayOpen(false)}
        />
      )}

      {/* Delete */}
      {deleteOverlayOpen && (
        <DeleteConfirmation
          onConfirm={handleRequestDeleted}
          setIsOverlayOpen={setDeleteOverlayOpen}
        />
      )}
    </div>
  );
};

// === Fetchers ===
const fetchProperties = async () => {
  const response = await axios.get(`${config.API_URL}/v2/property`, {
    headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
  });
  return response.data.map((item) => ({
    value: item._id,
    label: item.name,
  }));
};

const fetchUnitsByProperty = async (propertyId) => {
  const response = await axios.get(`${config.API_URL}/v2/unit/property/${propertyId}`, {
    headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
  });
  return response.data;
};

// ✅ Fixed: returns full pagination object (not just results)
const fetchRequestsByProperty = async (propertyId, page = 1, limit = 10) => {
  if (!propertyId) return { results: [], totalPages: 0, totalResults: 0, page: 1 };

  try {
    const response = await axios.get(`${config.API_URL}/v2/request/property/${propertyId}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
      },
      params: {
        page,
        limit,
        sortBy: 'createdAt',
        sortOrder: 'desc',
        type: 'GatePass',
      },
    });

    console.log('✅ Fetched GatePass requests:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Error fetching gate pass requests for property:', propertyId, error);
    return { results: [], totalPages: 0, totalResults: 0, page: 1 };
  }
};

export default MGatePass;
